import React, { forwardRef } from 'react';
import dayjs from 'dayjs';
import ReportPaper from './report/ReportPaper';
import styles from './MealPlanReportTemplate.module.css';
import { formatPortion } from '../../pages/appointment/components/mealPlan/mealPlanModel';

/** Título, subtítulo e data da assinatura do plano (`job` = { plan }). */
export function mealPlanReportMeta({ plan }) {
  const createdAt = plan?.createdAt ? dayjs(plan.createdAt).format('DD/MM/YYYY') : '';
  const startsAt = plan?.startsAt ? dayjs(plan.startsAt).format('DD/MM/YYYY') : '';
  return {
    title: plan?.title || 'Plano alimentar',
    subtitle: `${startsAt ? `Início em ${startsAt}` : `Elaborado em ${createdAt}`}${startsAt && createdAt ? ` · elaborado em ${createdAt}` : ''}`,
    date: createdAt,
  };
}

/**
 * Miolo do PDF: refeições em lista vertical com o horário numa coluna à
 * esquerda (cada bloco inteiro numa página) e orientações gerais.
 */
export const MealPlanReportBody = ({ plan }) => {
  const meals = plan?.meals || [];

  return (
    <>
      {meals.map((meal) => (
        <div key={meal.key} className={styles.meal}>
          <span className={meal.time ? styles.mealTime : `${styles.mealTime} ${styles.mealTimeEmpty}`}>
            {meal.time || '–'}
          </span>
          <div className={styles.mealBody}>
            <div className={styles.mealHead}>
              <span className={styles.mealName}>{meal.name}</span>
            </div>
            {meal.note && <div className={styles.mealNote}>{meal.note}</div>}
            <ul className={styles.items}>
              {(meal.items || []).map((item) => {
                const portion = formatPortion(item);
                return (
                  <li key={item.key} className={styles.item}>
                    <div className={styles.itemRow}>
                      <span className={styles.itemFood}>{item.food}</span>
                      {portion && <span className={styles.itemPortion}>{portion}</span>}
                    </div>
                    {item.note && <div className={styles.itemNote}>{item.note}</div>}
                    {item.substitutes && item.substitutes.length > 0 && (
                      <ul className={styles.substitutes}>
                        {item.substitutes.map((sub) => {
                          const subPortion = formatPortion(sub);
                          return (
                            <li key={sub.key} className={styles.substitute}>
                              <span className={styles.substituteTag}>ou</span>
                              {sub.food}{subPortion && `, ${subPortion}`}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      ))}

      {plan?.guidelines && (
        <div className={styles.guidelines}>
          <div className={styles.guidelinesTitle}>Orientações gerais</div>
          <div className={styles.guidelinesText}>{plan.guidelines}</div>
        </div>
      )}
    </>
  );
};

/**
 * PDF do plano alimentar entregue ao paciente: o papel compartilhado dos
 * relatórios (cabeçalho da clínica, paciente e profissional, assinatura e
 * rodapé) com o miolo do plano. Renderizado fora da tela e convertido pelo
 * CreatePDF (html2pdf).
 *
 * `professional` é o recurso da clínica (Label, Regulator), ou null.
 */
const MealPlanReportTemplate = forwardRef(({ plan, patient, companyData, professional }, ref) => {
  const meta = mealPlanReportMeta({ plan });
  return (
    <ReportPaper
      ref={ref}
      companyData={companyData}
      professional={professional}
      patientName={patient?.name}
      title={meta.title}
      subtitle={meta.subtitle}
      date={meta.date}
    >
      <MealPlanReportBody plan={plan} />
    </ReportPaper>
  );
});

MealPlanReportTemplate.displayName = 'MealPlanReportTemplate';

export default MealPlanReportTemplate;
